import type {
  LibraryItemKind,
  LibraryItemView,
} from '@/lib/api/generated/model';

/**
 * The one file that makes a folder an item of that kind.
 *
 * The same convention the service's `layout.py` states, repeated here because
 * the page has to ask for a file by name before it has read one. If the
 * convention ever changes it changes in the service first; the catalog's file
 * list is what keeps this honest, since a name that is not in it is refused.
 */
const REQUIRED_FILE: Record<LibraryItemKind, string> = {
  agent: 'AGENT.md',
  skill: 'SKILL.md',
  workflow: 'workflow.yaml',
};

/** The file whose text is the item's own words, when the library holds it. */
export function ownFileOf(item: LibraryItemView): string | undefined {
  const wanted = REQUIRED_FILE[item.kind];
  return (item.files ?? []).some((f) => f.path === wanted) ? wanted : undefined;
}

/** The item's other files, in the order the catalog lists them. */
export function otherFilesOf(item: LibraryItemView) {
  const own = REQUIRED_FILE[item.kind];
  return (item.files ?? []).filter((f) => f.path !== own);
}

/** Bytes as a reader reads them: "412 B", "1.4 KB". */
export function readableSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
